import type { PessoaComAcesso, TipoDeNotificacao } from './notificacoesApi'

/** O que o sino e as @menções decidem, sem React (testável à parte). */

export const NOMES_DOS_TIPOS: Record<TipoDeNotificacao, string> = {
  atribuido: 'Atribuição',
  comentario: 'Comentário',
  mencao: 'Menção',
  estado: 'Estado',
  prazo: 'Prazo',
  corrida: 'Corrida de deteção',
  partilha: 'Partilha',
}

/** O número no sino: até 9, depois "9+". */
export function textoDoContador(n: number): string {
  if (n <= 0) return ''
  return n > 9 ? '9+' : String(n)
}

/** "agora", "há 5 min", "há 3 h", "ontem", "há 4 dias" ou a data. */
export function tempoRelativo(iso: string, agora: Date = new Date()): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const s = Math.max(0, (agora.getTime() - d.getTime()) / 1000)
  if (s < 60) return 'agora'
  if (s < 3600) return `há ${Math.floor(s / 60)} min`
  if (s < 86400) return `há ${Math.floor(s / 3600)} h`
  const dias = Math.floor(s / 86400)
  if (dias === 1) return 'ontem'
  if (dias < 7) return `há ${dias} dias`
  return d.toLocaleDateString('pt-PT')
}

/** `/~/resto?x` -> `/<slug>/resto?x`: o `~` das ligações das notificações
 *  para quem é de fora é o workspace da sessão de quem abre. */
export function caminhoNoWorkspace(pathname: string, search: string, slug: string): string {
  const resto = pathname.replace(/^\/~\/?/, '')
  return `/${slug}/${resto}${search}`
}

// --- @menções ----------------------------------------------------------------

export interface MencaoEmCurso {
  /** Posição do @. */
  inicio: number
  /** O que já se escreveu depois do @. */
  consulta: string
}

const PALAVRA = /[\p{L}\p{N}_]/u

/** A menção que se está a escrever no cursor, ou null. Um @ no meio de
 *  uma palavra (um endereço de email) não abre sugestões. */
export function mencaoEmCurso(texto: string, cursor: number): MencaoEmCurso | null {
  const antes = texto.slice(0, cursor)
  const at = antes.lastIndexOf('@')
  if (at < 0) return null
  if (at > 0 && PALAVRA.test(antes[at - 1])) return null
  const consulta = antes.slice(at + 1)
  if (consulta.length > 40 || /[\n@]/.test(consulta)) return null
  // Duas palavras já escritas e um espaço a seguir: a menção acabou.
  if (/\s\S+\s$/.test(consulta)) return null
  return { inicio: at, consulta }
}

function normal(t: string): string {
  return t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}

/** As pessoas que começam pelo que se escreveu (nome, palavra do nome
 *  ou email), sem distinguir acentos nem maiúsculas. */
export function filtrarPessoas(
  pessoas: readonly PessoaComAcesso[],
  consulta: string,
  maximo = 6,
): PessoaComAcesso[] {
  const q = normal(consulta.trim())
  const fora = pessoas.filter((p) => {
    if (!q) return true
    const nome = normal(p.nome ?? '')
    const email = normal(p.email)
    return nome.startsWith(q) || email.startsWith(q) || nome.split(/\s+/).some((w) => w.startsWith(q))
  })
  return fora.slice(0, maximo)
}

/** Como a pessoa fica escrita no comentário: "@Nome Apelido" (o servidor
 *  reconhece o nome), ou "@email" quando não há nome. */
export function textoDaMencao(p: PessoaComAcesso): string {
  const nome = (p.nome ?? '').trim().replace(/\s+/g, ' ')
  return `@${nome || p.email}`
}

/** Substitui a menção em curso pela pessoa escolhida. */
export function aplicarMencao(
  texto: string,
  m: MencaoEmCurso,
  cursor: number,
  p: PessoaComAcesso,
): { texto: string; cursor: number } {
  const inserido = `${textoDaMencao(p)} `
  const novo = texto.slice(0, m.inicio) + inserido + texto.slice(cursor)
  return { texto: novo, cursor: m.inicio + inserido.length }
}

// --- Mapa especialidade -> pessoas --------------------------------------------

export function siglaValida(s: string): boolean {
  return /^[A-Z0-9]{2,8}$/.test(s)
}

/** As especialidades a mostrar: as sugeridas e as que o mapa já tem. */
export function siglasDoMapa(mapa: Record<string, string[]>, sugeridas: readonly string[]): string[] {
  const extra = Object.keys(mapa).filter((s) => !sugeridas.includes(s)).sort()
  return [...sugeridas, ...extra]
}

/** Põe ou tira uma pessoa de uma especialidade. */
export function alternarNoMapa(
  mapa: Record<string, string[]>,
  sigla: string,
  pessoa: string,
): Record<string, string[]> {
  const actual = mapa[sigla] ?? []
  const lista = actual.includes(pessoa) ? actual.filter((x) => x !== pessoa) : [...actual, pessoa]
  const fora = { ...mapa }
  if (lista.length) fora[sigla] = lista
  else delete fora[sigla]
  return fora
}
